import Case from "case";

import { ParsedProspect } from "../parser";
import { ProspectCellValue, ProspectRow } from "../types";

const topLevelFields = [
  "projectName",
  "submissionTime",
  "totalScores",
  "projectDeveloper",
  "email",
] as const;

export const hiddenQuestionnaireFields = new Set([
  "version",
  "isValidSubmit",
  "additionalFields",
  "happyToWorkWithEarthly",
]);

const questionnaireFieldOrder = [
  "entityRole",
  "projectLocation",
  "projectDescription",
  "registryLink",
  "ecosystemType",
  "projectType",
  "removalPercentage",
  "averageAnnualVolume",
  "projectStage",
  "registry",
  "vintageVolume",
  "methodology",
  "issuanceTiming",
  "socialDesign",
  "percentageRevenueShare",
  "socialDetail",
  "biodiversityDesignAndCobenefits",
  "biodiversityDetail",
  "commercialIntent",
  "shapefile",
  "additionalInfo",
] as const;

const questionnaireFieldOrderSet = new Set<string>(questionnaireFieldOrder);
export const submissionTimeField = "submissionTime";
export const totalScoresField = "totalScores";
export const prestoneReportField = "prestoneReport";
export const scoreFieldPrefix = "score.";

const scoreFieldOrder = [
  "social",
  "projectStage",
  "projectType",
  "issuanceTiming",
  "scale",
] as const;

export const scoreMaxValues: Record<string, number> = {
  social: 25,
  projectStage: 10,
  projectType: 20,
  issuanceTiming: 30,
  scale: 15,
};
export type ProspectFilterVariant =
  "text" | "numberRange" | "dateRange" | "boolean" | "multiSelect" | "none";

export const MULTI_SELECT_FIELD_IDS: ReadonlySet<string> = new Set([
  "entityRole",
  "ecosystemType",
  "projectType",
  "projectStage",
  "registry",
  "socialDesign",
  "commercialIntent",
  "projectLocation",
  "averageAnnualVolume",
  "issuanceTiming",
]);

type NormalizedProspect = {
  id: string;
  fields: Record<string, ProspectCellValue>;
};

export type ProspectTableFieldDefinition = {
  field: string;
  headerName: string;
  minWidth: number;
  type: "string" | "number" | "boolean";
  isSubmissionTime: boolean;
  isExpandableText: boolean;
  filterVariant: ProspectFilterVariant;
};

export type ProspectTableModel = {
  rows: ProspectRow[];
  fieldDefinitions: ProspectTableFieldDefinition[];
};

export const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const splitFieldPath = (field: string) => {
  return field.split(".");
};

const normalizeFieldSegment = (segment: string) => {
  return segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
};

const formatFieldSegment = (segment: string) => {
  const normalizedSegment = normalizeFieldSegment(segment);

  return normalizedSegment ? Case.title(normalizedSegment) : "";
};

export const humanizeFieldName = (field: string) => {
  if (field === submissionTimeField) {
    return "Completed at";
  }

  if (field.startsWith(scoreFieldPrefix)) {
    const scoreName = field.slice(scoreFieldPrefix.length);
    return `${formatFieldSegment(scoreName)} Score`;
  }

  return splitFieldPath(field)
    .map(formatFieldSegment)
    .filter(Boolean)
    .join(" / ");
};

const formatObjectSummary = (value: Record<string, unknown>) => {
  if (typeof value.filename === "string" && value.filename.trim()) {
    return value.filename;
  }

  if (typeof value.url === "string" && value.url.trim()) {
    return value.url;
  }

  const parts = Object.entries(value)
    .map(([key, entry]) => {
      const formattedValue = formatProspectValue(entry);

      if (formattedValue === null) {
        return null;
      }

      return `${humanizeFieldName(key)}: ${formattedValue}`;
    })
    .filter((entry): entry is string => entry !== null);

  return parts.length ? parts.join(", ") : null;
};

export const formatProspectValue = (value: unknown): ProspectCellValue => {
  if (value === null || value === undefined) {
    return null;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    const formattedValues = value
      .map((entry) => formatProspectValue(entry))
      .filter((entry): entry is string | number | boolean => entry !== null);

    return formattedValues.length
      ? formattedValues.map(String).join(", ")
      : null;
  }

  if (isRecord(value)) {
    for (const preferredKey of ["answer", "value", "label", "text"]) {
      if (value[preferredKey] !== undefined) {
        return formatProspectValue(value[preferredKey]);
      }
    }

    return formatObjectSummary(value);
  }

  return String(value);
};

export const formatScoreValue = (score: number, max: number | undefined) => {
  return max === undefined ? String(score) : `${score} / ${max}`;
};

export const formatSubmissionTime = (submissionTime: number) => {
  return new Date(submissionTime).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getQuestionnaireAnswers = (prospect: ParsedProspect) => {
  return isRecord(prospect.questionaireAnswers)
    ? prospect.questionaireAnswers
    : {};
};

const normalizeProspect = (prospect: ParsedProspect): NormalizedProspect => {
  const questionnaireAnswers = getQuestionnaireAnswers(prospect);
  const fields = Object.fromEntries(
    topLevelFields.map((field) => [
      field,
      formatProspectValue(prospect[field]),
    ]),
  ) as Record<string, ProspectCellValue>;

  if (prospect.scores) {
    Object.entries(prospect.scores).forEach(([key, value]) => {
      fields[`${scoreFieldPrefix}${key}`] = value;
    });
  }

  Object.entries(questionnaireAnswers).forEach(([key, value]) => {
    if (hiddenQuestionnaireFields.has(key)) {
      return;
    }

    fields[key] = formatProspectValue(value);
  });

  return {
    id: prospect.submissionId,
    fields,
  };
};

const scoreFieldOrderSet = new Set<string>(scoreFieldOrder);

export const orderScoreKeys = (keys: Iterable<string>) => {
  const keySet = new Set(keys);

  return [
    ...scoreFieldOrder.filter((key) => keySet.has(key)),
    ...[...keySet].filter((key) => !scoreFieldOrderSet.has(key)),
  ];
};

const getScoreFields = (prospectList: NormalizedProspect[]) => {
  const scoreKeys = new Set<string>();

  prospectList.forEach((prospect) => {
    Object.keys(prospect.fields).forEach((key) => {
      if (key.startsWith(scoreFieldPrefix)) {
        scoreKeys.add(key.slice(scoreFieldPrefix.length));
      }
    });
  });

  return orderScoreKeys(scoreKeys).map((key) => `${scoreFieldPrefix}${key}`);
};

const getQuestionnaireFields = (prospectList: NormalizedProspect[]) => {
  const fields = new Set<string>();

  prospectList.forEach((prospect) => {
    Object.keys(prospect.fields).forEach((key) => {
      if (
        !topLevelFields.includes(key as (typeof topLevelFields)[number]) &&
        !key.startsWith(scoreFieldPrefix)
      ) {
        fields.add(key);
      }
    });
  });

  const discoveredFields = [...fields];

  return [
    ...questionnaireFieldOrder.filter((field) => fields.has(field)),
    ...discoveredFields.filter(
      (field) => !questionnaireFieldOrderSet.has(field),
    ),
  ];
};

const getColumnWidth = (
  field: string,
  type: ProspectTableFieldDefinition["type"],
) => {
  if (field === submissionTimeField) {
    return 400;
  }

  if (field === totalScoresField) {
    return 200;
  }

  if (field.startsWith(scoreFieldPrefix)) {
    return 250;
  }

  if (["projectName", "projectDeveloper", "email"].includes(field)) {
    return 220;
  }

  if (
    [
      "methodology",
      "socialDesign",
      "additionalInfo",
      "projectDescription",
      "shapefile",
    ].includes(field)
  ) {
    return 260;
  }

  if (type === "number") {
    return 200;
  }

  return 180;
};

const getFieldType = (
  field: string,
  prospectList: NormalizedProspect[],
): ProspectTableFieldDefinition["type"] => {
  if (field === submissionTimeField || field === totalScoresField) {
    return "number";
  }

  for (const prospect of prospectList) {
    const fieldValue = prospect.fields[field];

    if (typeof fieldValue === "boolean") {
      return "boolean";
    }

    if (typeof fieldValue === "number") {
      return "number";
    }

    if (typeof fieldValue === "string") {
      return "string";
    }
  }

  return "string";
};

const getFilterVariant = (
  field: string,
  type: ProspectTableFieldDefinition["type"],
): ProspectFilterVariant => {
  if (field === submissionTimeField) {
    return "dateRange";
  }

  if (field === prestoneReportField) {
    return "none";
  }

  if (type === "number") {
    return "numberRange";
  }

  if (type === "boolean") {
    return "boolean";
  }

  if (MULTI_SELECT_FIELD_IDS.has(field)) {
    return "multiSelect";
  }

  return "text";
};

const isTopLevelField = (field: string) => {
  return topLevelFields.includes(field as (typeof topLevelFields)[number]);
};

const getIsExpandableText = (
  field: string,
  type: ProspectTableFieldDefinition["type"],
  filterVariant: ProspectFilterVariant,
) => {
  return (
    type === "string" && filterVariant === "text" && !isTopLevelField(field)
  );
};

const buildFieldDefinitions = (
  prospectList: NormalizedProspect[],
): ProspectTableFieldDefinition[] => {
  const [projectNameField, ...otherTopLevelFields] = topLevelFields;
  const orderedFields = [
    projectNameField,
    prestoneReportField,
    ...otherTopLevelFields,
    ...getScoreFields(prospectList),
    ...getQuestionnaireFields(prospectList),
  ];

  return orderedFields.map((field) => {
    const type = getFieldType(field, prospectList);
    const filterVariant = getFilterVariant(field, type);

    return {
      type,
      field,
      headerName: humanizeFieldName(field),
      minWidth: getColumnWidth(field, type),
      isSubmissionTime: field === submissionTimeField,
      isExpandableText: getIsExpandableText(field, type, filterVariant),
      filterVariant,
    };
  });
};

const buildRows = (prospectList: NormalizedProspect[]): ProspectRow[] => {
  return prospectList.map((prospect) => {
    return {
      id: prospect.id,
      ...prospect.fields,
    };
  });
};

export const buildProspectTableModel = (
  prospectList: ParsedProspect[],
): ProspectTableModel => {
  const normalizedProspects = prospectList.map(normalizeProspect);

  return {
    rows: buildRows(normalizedProspects),
    fieldDefinitions: buildFieldDefinitions(normalizedProspects),
  };
};
